import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'

import { enableLoginWithUsername } from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   Config,
   enableBackendBaseUrlChange
} from '@flowwai-frontend/bps-officer-web/env-config'
import {
   LeftAlignedLoginLayoutConfig,
   useClientConfig
} from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import {
   loginMachineTags,
   NO_OF_OTP_BOXES
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'
import {
   getIsSessionLogoutModalOpen,
   setIsSessionLogoutModalOpen
} from '../../utils/StorageUtils'

import EnvironmentSelector from '../EnvironmentSelector/EnvironmentSelector'
import { OtpVerificationStep } from '../OtpVerificationStep/OtpVerificationStep'
import SessionLogoutModal from '../SessionLogoutModal/SessionLogoutModal'
import LoginWithEmail from '../UserLogin/LoginWithEmail'
import LoginWithUsername from '../UserLogin/LoginWithUsername'

import './index.css'
import * as Styles from './styles'

interface LoginProps {
   loginMachineState: LoginMachineState
   loginMachineSend: LoginMachineSend
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
}

const Login = (props: LoginProps): ReactElement => {
   const { loginMachineState, loginMachineSend, timerRef } = props

   const clientConfig = useClientConfig()
   const { login: clientLoginConfig, loginLayout } = clientConfig

   //TODO: need to simplify the logic
   const isModalOpen = getIsSessionLogoutModalOpen() === 'true' ? true : false

   const [isOpenSessionModal, setIsOpenSessionModal] = useState(isModalOpen)

   const [showLoginTransition, setShowLoginTransition] = useState(false)

   const showEnvSelectors = enableBackendBaseUrlChange(Config)

   const { theme } = useAppTheme()
   const { common: loginTheme } = theme.auth.login

   const onCloseSessionModal = (): void => {
      setIsSessionLogoutModalOpen(false)
      setIsOpenSessionModal(false)
   }

   const renderLoginBasedOnMachineState = (): ReactElement | null => {
      const isLoginWithEmail = loginMachineState.hasTag(
         loginMachineTags.loginScreen
      )

      const isLoginWithOTP = loginMachineState.hasTag(
         loginMachineTags.otpScreen
      )
      if (isLoginWithEmail) {
         return (
            <div
               className={cn(
                  { 'login-enter-transition': showLoginTransition },
                  'w-[360px]'
               )}
            >
               {enableLoginWithUsername ? (
                  <LoginWithUsername
                     loginMachineSend={loginMachineSend}
                     loginMachineState={loginMachineState}
                     clientConfig={clientConfig}
                  />
               ) : (
                  <LoginWithEmail
                     loginMachineSend={loginMachineSend}
                     loginMachineState={loginMachineState}
                     clientConfig={clientConfig}
                  />
               )}
               {showEnvSelectors && <EnvironmentSelector />}
            </div>
         )
      } else if (isLoginWithOTP) {
         return (
            <OtpVerificationStep
               otpFieldsCount={NO_OF_OTP_BOXES}
               loginMachineState={loginMachineState}
               loginMachineSend={loginMachineSend}
               timerRef={timerRef}
               setShowLoginTransition={setShowLoginTransition}
            />
         )
      }
      return null
   }

   const renderDefaultLayout = (): ReactElement => (
      <div className={cn(Styles.containerStyles, loginTheme.loginBGColor)}>
         <div className={cn(Styles.headerContainerStyles)}>
            <img
               alt='App Logo'
               src={clientLoginConfig.logoURL}
               style={{ width: `${clientLoginConfig.logoWidth}px` }}
            />
         </div>
         <div
            className={cn(Styles.bodyContainerStyles, loginTheme.loginBGColor)}
         >
            {renderLoginBasedOnMachineState()}
         </div>
         <p
            className={cn(
               Styles.footerDetailStyles,
               loginTheme.footerDetailsTextColor
            )}
         >
            {clientConfig.rightRegistered}
         </p>
         <div
            className={cn(Styles.footerBackgroundImageContainerStyles)}
            style={{
               backgroundImage: `url(${loginLayout.footerImageUrl})`,
               backgroundSize: 'contain',
               height: `${loginLayout.footerHeight}px`
            }}
         />
      </div>
   )

   const renderLeftAlignedLayout = (): ReactElement => {
      const layoutConfig = loginLayout as LeftAlignedLoginLayoutConfig
      return (
         <div
            className={cn(
               Styles.leftAlignedContainerStyles,
               loginTheme.loginBGColor
            )}
            style={{
               background: `url(${layoutConfig.poster.patternUrl}) right no-repeat`,
               backgroundSize: '55%'
            }}
         >
            <div className='flex flex-1'>
               <div className={'w-[45%] flex flex-col items-center h-full'}>
                  <div className={cn(Styles.bodyContainerStyles)}>
                     {renderLoginBasedOnMachineState()}
                  </div>
               </div>
               <div className={Styles.leftAlignedRightContainerStyles}>
                  <div className={Styles.leftAlignedRightInnerContainerStyles}>
                     <img
                        src={layoutConfig.poster.imageUrl}
                        alt='poster'
                        className={Styles.posterImageStyles}
                     />
                  </div>
                  <p
                     className={cn(
                        Styles.footerDetailStyles,
                        loginTheme.footerDetailsTextColor,
                        Styles.leftAlignedLayoutFooterStyles
                     )}
                  >
                     {clientConfig.rightRegistered}
                  </p>
               </div>
            </div>
            <div
               className={cn(Styles.footerBackgroundImageContainerStyles)}
               style={{
                  backgroundImage: `url(${layoutConfig.footerImageUrl})`,
                  backgroundSize: 'contain',
                  height: `${layoutConfig.footerHeight}px`
               }}
            />
         </div>
      )
   }

   const renderBasedOnLayout = (): ReactElement => {
      switch (loginLayout.type) {
         case 'LEFT_ALIGNED':
            return renderLeftAlignedLayout()
         case 'DEFAULT':
         default:
            return renderDefaultLayout()
      }
   }

   return (
      <>
         {renderBasedOnLayout()}
         <SessionLogoutModal
            isOpen={isOpenSessionModal}
            onCloseModal={onCloseSessionModal}
         />
      </>
   )
}

export default observer(Login)
